import clientPortalScreens from "../../clientPortalMedia";

const clientPortal = {
  id: "client-portal",
  slug: "client-portal",
  category: "Applications",
  navTitle: "Redesigning the Client Portal",
  title: "Redesigning the Client Portal with Oak",
  introduction: [
    "The Co-operators client portal is a critical service channel for policyholders, providing access to claims, policy documents, billing, and account information. I led the redesign using Oak, improving navigation, accessibility, and consistency while working within the constraints of legacy back-end systems.",
  ],
  challenge: [
    {
      title: "A fragmented experience",
      paragraphs: [
        "The portal had inconsistent interfaces and interaction patterns across different services, which made the experience feel disconnected.",
      ],
    },
    {
      title: "Difficult navigation",
      paragraphs: [
        "Customers struggled to find common tasks such as claims tracking, policy documents, billing, and account information.",
      ],
    },
    {
      title: "Accessibility gaps",
      paragraphs: [
        "The experience created barriers for people using keyboards, screen readers, and other assistive technologies.",
      ],
    },
    {
      title: "Legacy constraints",
      paragraphs: [
        "The new experience still had to connect to older systems that could not simply be replaced.",
      ],
    },
  ],
  process: [
    {
      title: "Understanding where customers were struggling",
      paragraphs: [
        "I used several sources of evidence rather than relying on stakeholder assumptions.",
      ],
      bullets: [
        "Conducted usability testing with existing portal users.",
        "Reviewed support inquiries to identify recurring issues.",
        "Mapped common journeys across claims, policy access, documents, and billing.",
        "Prioritized problems based on frequency and customer impact.",
      ],
    },
    {
      title: "Simplifying the structure",
      paragraphs: [
        "I reorganized the experience around the tasks customers needed to complete most often.",
      ],
      bullets: [
        "Simplified navigation and information architecture.",
        "Introduced a dashboard-style homepage with clearer access to key services.",
        "Applied Oak components to create more consistent patterns and hierarchy.",
        "Worked with content specialists to improve labels, instructions, and supporting information.",
      ],
    },
    {
      title: "Designing accessibility into the experience",
      paragraphs: [
        "Accessibility was addressed throughout design and development.",
      ],
      bullets: [
        "Supported keyboard navigation, visible focus states, screen-reader use, and appropriate contrast.",
        "Reused Oak’s accessibility patterns where possible.",
        "Tested the experience iteratively with customers and internal users.",
        "Refined the design as technical and usability issues emerged.",
      ],
    },
    {
      title: "Working within the existing environment",
      paragraphs: [
        "The desired experience had to be balanced against integration and delivery realities.",
      ],
      bullets: [
        "Worked closely with development teams to understand technical constraints.",
        "Identified where Oak components could be implemented directly and where adaptation was necessary.",
        "Coordinated with product and business stakeholders to balance customer needs with operational priorities.",
      ],
    },
  ],
  outcomes: [
    {
      title: "A clearer customer experience",
      description:
        "The redesign created a more coherent experience across claims, billing, policy, and account-management tasks.",
    },
    {
      title: "Improved self-service",
      description:
        "Simplified navigation and more direct access to common tasks contributed to a 35% reduction in support inquiries.",
    },
    {
      title: "Greater usability",
      description:
        "Customer surveys showed a 40% improvement in ease of use and task completion.",
    },
    {
      title: "Improved accessibility",
      description:
        "The redesigned portal achieved WCAG 2.1 compliance and provided stronger support for people using assistive technologies.",
    },
    {
      title: "Validation of Oak",
      description:
        "The work demonstrated that Oak could support a complex authenticated product, not just public-facing websites or isolated components.",
    },
  ],
  reflection: [
    "The portal reinforced that simplifying an experience does not remove complexity. It means absorbing more of that complexity behind the interface, so customers do not have to deal with it.",
    "Oak gave us a strong foundation, but the design system was not the solution on its own. Research, content, technical collaboration, and repeated testing were still required to make the experience work within a complicated legacy environment.",
    "The project also proved that Oak could operate as an enterprise product platform, not simply as a component library.",
  ],
  media: {
    blocks: [
      {
        id: "client-portal-responsive-composition",
        type: "composition",
        placement: "afterIntro",
        className: "work-page__media-block--integrated-opening",
        items: clientPortalScreens,
        aspectRatio: "3 / 2",
        label: "Co-operators client portal across desktop, tablet, and mobile",
        galleryLabel: "Co-operators client portal experience gallery",
        alt: "Co-operators client portal and payment experiences across desktop, tablet, and mobile",
      },
      {
        id: "client-portal-responsive-flow",
        type: "grid",
        placement: "afterChallenge",
        title: "One complex service, designed across devices",
        description:
          "The add-driver journey applies the same hierarchy, guidance, and task progression across desktop and mobile without reducing the experience to a scaled-down screen.",
        layout: "default",
        options: {
          maxWidth: 1000,
          tileMin: 320,
          thumbHeight: 440,
          thumbFit: "contain",
          modalMaxWidth: 1100,
          modalMaxHeight: "min(90vh, 960px)",
        },
        items: [
          {
            id: "add-driver-desktop",
            src: "/images/cooperators/case-studies/client-portal/evidence/add-driver-desktop.webp",
            alt: "Co-operators add-driver journey on desktop with a five-step progress indicator and structured driver-details form",
            caption:
              "Desktop: clear task progression and grouped driver details",
          },
          {
            id: "add-driver-mobile",
            src: "/images/cooperators/case-studies/client-portal/evidence/add-driver-mobile.webp",
            alt: "Co-operators add-driver journey on mobile with the same progress, form hierarchy, and contextual guidance",
            caption:
              "Mobile: the same hierarchy and guidance adapted to a narrow viewport",
          },
        ],
      },
      {
        id: "client-portal-oak-patterns",
        type: "grid",
        placement: "afterApproach",
        title: "Oak patterns proven through product decisions",
        description:
          "The portal acted as a practical stress test for Oak. Progressive disclosure and feedback states were refined as reusable patterns while solving immediate product problems.",
        layout: "default",
        options: {
          maxWidth: 1000,
          tileMin: 320,
          thumbHeight: 360,
          thumbFit: "contain",
          modalMaxWidth: 1080,
          modalMaxHeight: "min(88vh, 900px)",
        },
        items: [
          {
            id: "simplified-accordion-pattern",
            src: "/images/cooperators/case-studies/client-portal/evidence/simplified-accordion-pattern.webp",
            alt: "Closed and open states of a simplified Oak accordion for contextual insurance guidance",
            caption:
              "Progressive disclosure keeps supporting guidance available without overwhelming the form",
          },
          {
            id: "password-strength-feedback-states",
            src: "/images/cooperators/case-studies/client-portal/evidence/password-strength-feedback-states.webp",
            alt: "Oak password-strength pattern showing default, invalid, weak, improving, and strong feedback states",
            caption:
              "Password feedback distinguishes validity from strength and supports the next action",
          },
        ],
      },
    ],
  },
};

export default clientPortal;
